import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  ScrollText,
  Scale,
  FileBarChart2,
  Briefcase,
  Search,
  Calculator,
  CheckCircle2,
} from "lucide-react";
import { SERVICES } from "../lib/constants";
import { Button } from "../components/ui/button";

const ICONS = {
  "external-audit": ShieldCheck,
  "internal-audit": ScrollText,
  "tax-advisory": Calculator,
  "ifrs-advisory": FileBarChart2,
  "risk-compliance": Scale,
  "forensic-audit": Search,
  bookkeeping: Briefcase,
};

export default function Services() {
  const { slug } = useParams();
  const focused = slug ? SERVICES.find((s) => s.slug === slug) : null;

  return (
    <div data-testid="page-services">
      {/* Header */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-16">
          <div className="overline rule-teal">Services</div>
          <h1 className="font-display text-5xl sm:text-6xl mt-6 leading-[1.02] text-[#0B2E5C] max-w-3xl">
            Audit, assurance & advisory — delivered to one standard.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#475569] leading-relaxed font-light">
            Seven service lines, each led by a senior partner. Independent on
            audit. Practical on advisory. Always evidence‑led.
          </p>
        </div>
      </section>

      {/* Services list */}
      <section className="bg-[#F8FAFC]" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 space-y-12">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.slug];
            const isFocused = focused && focused.slug === s.slug;
            return (
              <article
                key={s.slug}
                id={s.slug}
                data-testid={`service-detail-${s.slug}`}
                className={`bg-white border border-[#E2E8F0] p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-10 ${
                  isFocused ? "ring-2 ring-[#2BA8B8]" : ""
                }`}
              >
                <div className="lg:col-span-4">
                  <div className="flex items-center gap-4">
                    <span className="font-display text-3xl text-[#2BA8B8]">
                      0{i + 1}
                    </span>
                    <Icon className="h-7 w-7 text-[#0B2E5C]" strokeWidth={1.4} />
                  </div>
                  <h2 className="font-display text-3xl sm:text-4xl mt-6 text-[#0B2E5C] leading-tight">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-[#475569] font-light leading-relaxed">
                    {s.short}
                  </p>
                </div>
                <div className="lg:col-span-5 text-[#475569] font-light leading-relaxed">
                  <p>{s.description}</p>
                </div>
                <div className="lg:col-span-3">
                  <div className="overline">Deliverables</div>
                  <ul className="mt-4 space-y-3">
                    {s.deliverables.map((d, idx) => (
                      <li key={idx} className="flex gap-3 text-sm text-[#0B2E5C]">
                        <CheckCircle2 className="h-4 w-4 text-[#2BA8B8] mt-0.5 shrink-0" strokeWidth={1.5} />
                        <span className="font-light">{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B2E5C] text-white" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline !text-[#2BA8B8] rule-teal">Engage</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
              Not sure which service you need?
            </h2>
            <p className="mt-4 text-white/70 font-light text-lg max-w-2xl">
              We'll scope it together. Send us a short note about your situation
              and one of our partners will reply within a business day.
            </p>
          </div>
          <div className="lg:col-span-4">
            <Link to="/contact" data-testid="services-cta-contact">
              <Button className="w-full rounded-sm bg-[#2BA8B8] hover:bg-[#228592] text-white h-14 text-sm tracking-wide">
                Talk to a partner
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
